import { fetchWithCSRF } from "@/helpers/common/csrf";
import clsx from "clsx";
import { Check, Copy, Link2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

const API_URL = import.meta.env.VITE_VANDERHEIM_NINJA_API_URL;
const CONNECTIONS_URL = `${API_URL}/longhouse-api/village/twitch-connections/`;
const REFRESH_MS = 5000;

type ConnectionStatus = "connected" | "needs_reconnecting" | "not_connected";

interface Connection {
    key: string;
    label: string;
    account: string;
    status: ConnectionStatus;
    missing_scopes: string[];
}

interface ConnectionsResponse {
    connections: Connection[];
    failing_subscriptions: string[];
}

const statusBadges: Record<
    ConnectionStatus,
    { text: string; className: string }
> = {
    connected: {
        text: "Connected",
        className:
            "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300",
    },
    needs_reconnecting: {
        text: "Needs reconnecting",
        className:
            "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
    },
    not_connected: {
        text: "Not connected",
        className:
            "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
    },
};

export default function TwitchConnectionsPage() {
    const [data, setData] = useState<ConnectionsResponse | null>(null);
    const [links, setLinks] = useState<Record<string, string>>({});
    const [copied, setCopied] = useState("");
    const [failed, setFailed] = useState(false);

    const load = useCallback(async () => {
        try {
            const response = await fetchWithCSRF(CONNECTIONS_URL);
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }
            setData(await response.json());
            setFailed(false);
        } catch (error) {
            console.error("Error loading Twitch connections:", error);
            setFailed(true);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    // Show the result as soon as someone finishes a link in another browser.
    const waitingForLink = Object.keys(links).length > 0;
    useEffect(() => {
        if (!waitingForLink) {
            return;
        }
        const timer = setInterval(load, REFRESH_MS);
        return () => clearInterval(timer);
    }, [waitingForLink, load]);

    const createLink = async (key: string) => {
        try {
            const response = await fetchWithCSRF(
                `${CONNECTIONS_URL}${key}/link/`,
                { method: "POST" },
            );
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }
            const { url } = await response.json();
            setLinks((current) => ({ ...current, [key]: url }));
            setCopied("");
            setFailed(false);
        } catch (error) {
            console.error("Error creating connect link:", error);
            setFailed(true);
        }
    };

    const copyLink = async (key: string) => {
        await navigator.clipboard.writeText(links[key]);
        setCopied(key);
    };

    return (
        <div className="space-y-6 p-6">
            <div>
                <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                    Twitch Connections
                </h1>
                <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                    Get a link, then open it in a browser signed in to that
                    Twitch account or send it to the account's owner. Links work
                    once and expire after 15 minutes.
                </p>
            </div>

            {failed && (
                <p className="text-sm text-red-600 dark:text-red-400">
                    Something went wrong talking to the server.
                </p>
            )}

            <div className="grid gap-4 md:grid-cols-2">
                {data?.connections.map((connection) => {
                    const badge = statusBadges[connection.status];
                    const link = links[connection.key];
                    return (
                        <section
                            key={connection.key}
                            aria-label={connection.label}
                            className="space-y-3 rounded-lg border border-slate-200 p-4 dark:border-slate-700"
                        >
                            <div className="flex items-start justify-between gap-2">
                                <div>
                                    <h2 className="font-semibold text-slate-900 dark:text-slate-100">
                                        {connection.label}
                                    </h2>
                                    <p className="text-sm text-slate-600 dark:text-slate-400">
                                        {connection.account}
                                    </p>
                                </div>
                                <span
                                    className={clsx(
                                        "rounded-full px-2.5 py-1 text-xs font-medium",
                                        badge.className,
                                    )}
                                >
                                    {badge.text}
                                </span>
                            </div>

                            {connection.status === "needs_reconnecting" &&
                                connection.missing_scopes.length > 0 && (
                                    <p className="break-words text-sm text-slate-600 dark:text-slate-400">
                                        Missing:{" "}
                                        {connection.missing_scopes.join(", ")}
                                    </p>
                                )}

                            <button
                                type="button"
                                onClick={() => createLink(connection.key)}
                                className="flex items-center gap-2 rounded-lg bg-slate-200 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-300 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600"
                            >
                                <Link2 size={16} />
                                Get connect link
                            </button>

                            {link && (
                                <div className="flex items-center gap-2">
                                    <input
                                        readOnly
                                        value={link}
                                        aria-label={`${connection.label} connect link`}
                                        className="min-w-0 flex-1 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => copyLink(connection.key)}
                                        aria-label={`Copy ${connection.label} link`}
                                        className="rounded-lg bg-slate-200 p-2 text-slate-700 hover:bg-slate-300 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600"
                                    >
                                        {copied === connection.key ? (
                                            <Check size={16} />
                                        ) : (
                                            <Copy size={16} />
                                        )}
                                    </button>
                                </div>
                            )}
                        </section>
                    );
                })}
            </div>

            {data && data.failing_subscriptions.length > 0 && (
                <section className="rounded-lg border border-amber-300 p-4 dark:border-amber-700">
                    <h2 className="font-semibold text-slate-900 dark:text-slate-100">
                        Failing EventSub subscriptions
                    </h2>
                    <ul className="mt-2 list-disc pl-5 text-sm text-slate-600 dark:text-slate-400">
                        {data.failing_subscriptions.map((eventType) => (
                            <li key={eventType}>{eventType}</li>
                        ))}
                    </ul>
                </section>
            )}
        </div>
    );
}
